import clsx from 'clsx'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import config from 'constants/config'
import FlightSearchAirportModal from './FlightAirportSelect/FlightSearchAirportModal'
import FlightSearchResponsiveAirportField from './FlightAirportSelect/FlightSearchResponsiveAirportField'
import ModalContext from 'contexts/ModalContext'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import FlightSearchMenuMobile from './FlightSearchMenu/FlightSearchMenuMobile'
import { addDays } from '@luxuryescapes/lib-refunds/build/lib/date'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import FlightSearchTravellerSelectContent from './FlightTravellerSelect/FlightSearchTravellerSelectContent'
import { getAirportDisplayText } from 'lib/flights/flightUtils'
import useFlightDeal from 'hooks/Flights/useFlightDeal'

import FlightDealDuration from 'components/Flights/FlightMerchandising/FlightDealDuration/FlightDealDuration'
import FlightDealAgentHubDisclaimer from 'components/Flights/FlightMerchandising/FlightDealAgentHubDisclaimer/FlightDealAgentHubDisclaimer'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import BodyText from 'LuxKit/Typography/BodyText'
import { FlightsFareTypes } from 'constants/flight'
import styled from 'styled-components'
import TextButton from 'LuxKit/Button/TextButton'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LinePlusCircleIcon from 'LuxKit/Icons/line/LinePlusCircleIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Heading from 'LuxKit/Typography/Heading'
import {
  FlightSearchWidgetActions,
  createEmptyFlightSearchWidgetFlightItem,
} from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import type {
  FlightSearchWidgetFlightItem,
  FlightSearchWidgetInitialFocus,
} from 'contexts/Flights/FlightSearchWidget/types'
import { StandaloneFlightMenu } from 'contexts/Flights/FlightSearchWidget/types'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import LineArrowsSwapVerticalIcon from 'LuxKit/Icons/line/LineArrowsSwapVerticalIcon'
import IconButton from 'LuxKit/Button/IconButton'
import { rem } from 'polished'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { mediaQueryUp } from 'components/utils/breakpoint'
import FlightSearchMaxFlightsModal from './FlightSearchMaxFlightsModal'
import FlightSearchDateSelectSheet from './FlightSearchDateSelectSheet'
import FlightsVerticalDatePicker from './FlightsDatePicker/FlightsVerticalDatePicker'
import FlightsResultsDatePicker from './FlightsDatePicker/FlightsResultsDatePicker'
import { applyFlightSearchInitialFocus } from './applyFlightSearchInitialFocus'

const ButtonsContainer = styled(Group)`
  &.mobile-results-redesign {
    overflow: hidden;
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    border-radius: ${props => props.theme.borderRadius.L};
  }

  &.mobile-results-redesign .airport-select {
    align-self: stretch;
    background-color: transparent;
    border: 0;
    border-block-end: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    border-radius: 0;
    box-sizing: border-box;
    gap: 0;
    min-block-size: ${rem(48)};
    padding-block: ${rem(12)};
    padding-inline: ${rem(16)};
  }

  &.mobile-results-redesign .date-select {
    align-self: stretch;
    background-color: transparent;
    border: 0;
    border-radius: 0;
    box-sizing: border-box;
    min-block-size: ${rem(48)};
    padding-block: ${rem(12)};
    padding-inline: ${rem(16)};
  }

  & > button:not(:last-child) {
    border-bottom: 0;
  }
`

const AirportsContainer = styled(Group)`
  position: relative;

  & > button:not(.switch){
    border-bottom: 0;
  }

  &.mobile-results-redesign > button {
    min-block-size: ${rem(48)};
  }
`

const Form = styled(VerticalSpacer)`
  &.mobile-results-redesign {
    gap: ${rem(20)};
  }
`

const FlightHeader = styled(Group)`
  &.mobile-results-redesign {
    padding-inline-start: ${rem(12)};
  }
`

const ExchangeButtonContainer = styled.div`
  position: absolute;
  inset-inline-end: ${rem(16)};
  top: 50%;
  transform: translateY(-50%);
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: 50%;
`

interface Props {
  /**
   * Sets the airports to a 'read only' mode
   * Used when defaulting airport selections such as on flight deal pages
   */
  readOnlyAirports?: boolean
  initialFocus?: FlightSearchWidgetInitialFocus
  mobileResultsRedesign?: boolean
}

function FlightSearchWidgetMobileMultiCityForm(props: Props) {
  const { readOnlyAirports, initialFocus, mobileResultsRedesign } = props
  const intl = useIntl()
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)
  const departingRefs = useRef<Array<HTMLButtonElement | null>>([])
  const hasAppliedInitialFocus = useRef(false)
  const datePickerAnchorRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLDivElement | null>(null)
  const datePickerAnchorRef = useMemo(() => ({ current: datePickerAnchor }), [datePickerAnchor])
  const isTabletUp = useScreenSizeUp('tablet')
  const isTabletViewport = isTabletUp || (typeof window !== 'undefined' && window.matchMedia(mediaQueryUp.tablet).matches)

  const { fareType, flightDealId, flights } = state
  const [flightDeal] = useFlightDeal(flightDealId)
  const showModal = useContext(ModalContext)

  const travellersTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'M-8hnlubARNYJJXZe8dP',
        defaultMessage: 'Travellers',
        description: 'Title for travellers selection modal in flight search widget',
      }),
    [intl],
  )

  const onTravellerSelect = useCallback(() => {
    showModal(
      <FlightSearchMenuMobile title={travellersTitle} showTravellers>
        <FlightSearchTravellerSelectContent />
      </FlightSearchMenuMobile>,
    )
  }, [showModal, travellersTitle])

  const earliestBookingDate = useMemo(() => addDays(new Date(), 1), [])
  const finalBookingDate = useMemo(() => addDays(new Date(), 330), [])
  const featuredRanges = useMemo(() => {
    return flightDeal?.travelPeriods.map(period => ({
      start: new Date(period.departureDate),
      end: new Date(period.arrivalDate),
    }))
  }, [flightDeal])

  const dateSelectTitle = useMemo(
    () => mobileResultsRedesign ? intl.formatMessage({
      id: 'xcIuQafq7GU0ucPlHFHV',
      defaultMessage: 'Select dates',
      description: 'Title for the redesigned mobile and tablet flights date selection modal',
    }) : intl.formatMessage({
      id: 'C36_0JIKVCqvkVWZ8_o-',
      defaultMessage: 'When do you want to go?',
      description: 'Title for date selection modal in flight search widget',
    }),
    [intl, mobileResultsRedesign],
  )

  const addFlightLabel = useMemo(
    () => mobileResultsRedesign ? intl.formatMessage({
      id: 'I6OrwExD51z7UNbR-TMe',
      defaultMessage: 'Add another flight',
      description: 'Button text to add a further flight leg to a multi-city flight search',
    }) : intl.formatMessage({
      id: 'zZNaxnWtkPuAmxA5MOj_',
      defaultMessage: 'Add flight',
      description: 'Button text to add another flight in multi-city flight search',
    }),
    [intl, mobileResultsRedesign],
  )

  const departureAirportLabel = intl.formatMessage({
    id: 'qD7oW0qPSP3nIenQylK1',
    defaultMessage: 'From',
    description: 'Label for the departure airport input field in multi-city flight search form',
  })

  const arrivalAirportLabel = intl.formatMessage({
    id: '2UOCctw7EHd0XUmxtgr3',
    defaultMessage: 'To',
    description: 'Form field label for arrival airport field in flight search',
  })

  const departingDateLabel = intl.formatMessage({
    id: 'h715GKJI_T7chJO4FOob',
    defaultMessage: 'Departing',
    description: 'Form field label for departing date field in flight search',
  })

  const onDateSelect = useCallback(
    async(flightId: string) => {
      if (mobileResultsRedesign && isTabletViewport) {
        setDatePickerAnchor(datePickerAnchorRefs.current[flightId] ?? null)
        dispatch({
          type: FlightSearchWidgetActions.SET_ACTIVE_MENU,
          id: flightId,
          activeMenu: StandaloneFlightMenu.date,
          selectingDateType: 'departing',
        })
        return
      }

      const dates = await showModal<Record<string, string>>(
        <FlightSearchMenuMobile
          title={dateSelectTitle}
          showDates
          useApplyActions={mobileResultsRedesign}
        >
          <Group
            direction="vertical"
            verticalAlign="space-between"
            gap={12}
            fullHeight
          >
            {mobileResultsRedesign ? <FlightsResultsDatePicker
              minDate={earliestBookingDate}
              maxDate={finalBookingDate}
              id={flightId}
              featuredRanges={featuredRanges}
            /> : <FlightsVerticalDatePicker
              minDate={earliestBookingDate}
              maxDate={finalBookingDate}
              id={flightId}
              required
              featuredRanges={featuredRanges}
            />}
            <VerticalSpacer gap={12}>
              {flightDeal && <FlightDealDuration flightDeal={flightDeal} />}
              {isAgentHubEnabled && <FlightDealAgentHubDisclaimer />}
              {!mobileResultsRedesign && <BodyText variant="small" colour="light-foreground-alt">
                {intl.formatMessage({
                  id: 'Hnli7vLI09sRE0RyYhzo',
                  defaultMessage: 'Based on recent return prices (per adult) found',
                  description: 'Message about recent return prices in flight date picker',
                })}
              </BodyText>}
            </VerticalSpacer>
          </Group>
        </FlightSearchMenuMobile>,
      )

      const rowIndex = flights.findIndex(f => f.id === flightId)
      const pendingRows = rowIndex < flights.length - 1

      if (mobileResultsRedesign && dates && pendingRows) {
        departingRefs.current[rowIndex + 1]?.click()
        return
      }

      if (!mobileResultsRedesign && pendingRows) {
        departingRefs.current[rowIndex + 1]?.click()
      }

      if (dates && !pendingRows && !mobileResultsRedesign) {
        onTravellerSelect()
      }
    },
    [showModal, earliestBookingDate, finalBookingDate, featuredRanges, flightDeal, flights, mobileResultsRedesign, onTravellerSelect, dateSelectTitle, intl, dispatch, isTabletViewport],
  )

  const closeTabletDatePicker = useCallback(() => {
    dispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, activeMenu: undefined })
  }, [dispatch])

  const onArrivalSelect = useCallback(
    async(flightId: string) => {
      if (mobileResultsRedesign && isTabletViewport) {
        dispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, id: flightId, activeMenu: StandaloneFlightMenu.arrivalAirport })
        return
      }
      const airport = await showModal<App.AirportLocation | undefined>(
        <FlightSearchAirportModal direction="arrival" id={flightId} />,
      )

      if (airport) {
        onDateSelect(flightId)
      }
    },
    [onDateSelect, showModal, mobileResultsRedesign, isTabletViewport, dispatch],
  )

  const handleRemoveFlight = useCallback(
    (flightId: string) => {
      if (mobileResultsRedesign && flights.length <= 2) {
        return
      }

      dispatch({
        type: FlightSearchWidgetActions.REMOVE_FLIGHT,
        payload: flightId,
      })
    },
    [dispatch, flights.length, mobileResultsRedesign],
  )

  const handleAddFlight = useCallback(() => {
    if (flights.length === config.FLIGHTS_MULTI_CITY_MAX_FLIGHTS) {
      showModal<boolean>(<FlightSearchMaxFlightsModal />)
      return
    }

    const previousArrivalAirport = flights[flights.length - 1]?.arrivalAirport
    const item: FlightSearchWidgetFlightItem = createEmptyFlightSearchWidgetFlightItem({
      ...(mobileResultsRedesign && { departureAirport: previousArrivalAirport }),
    })

    dispatch({
      type: FlightSearchWidgetActions.ADD_FLIGHT,
      payload: item,
    })
  }, [dispatch, flights, mobileResultsRedesign, showModal])

  const onDepartureSelect = useCallback(
    async(flightId: string) => {
      if (readOnlyAirports) return
      if (mobileResultsRedesign && isTabletViewport) {
        dispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, id: flightId, activeMenu: StandaloneFlightMenu.departAirport })
        return
      }

      const airport = await showModal<App.AirportLocation | undefined>(
        <FlightSearchAirportModal direction="departure" id={flightId} />,
      )

      if (airport) {
        onArrivalSelect(flightId)
      }
    },
    [onArrivalSelect, readOnlyAirports, showModal, mobileResultsRedesign, isTabletViewport, dispatch],
  )

  const onSwapAirports = useCallback((flightId: string) => {
    const currentFlight = flights.find(flight => flight.id === flightId)
    if (!currentFlight) return
    const departure = currentFlight.departureAirport
    dispatch({ type: FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT, id: flightId, airport: currentFlight.arrivalAirport })
    dispatch({ type: FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT, id: flightId, airport: departure })
  }, [flights, dispatch])

  const showRemoveButton = flights.length > (mobileResultsRedesign ? 2 : 1)

  useEffect(() => {
    if (!initialFocus || hasAppliedInitialFocus.current) {
      return
    }

    const timeoutId = setTimeout(() => {
      hasAppliedInitialFocus.current = true
      applyFlightSearchInitialFocus(initialFocus, {
        departureAirport: onDepartureSelect,
        arrivalAirport: onArrivalSelect,
        departureDate: onDateSelect,
      })
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [initialFocus, onArrivalSelect, onDateSelect, onDepartureSelect])

  return <>
    <Form className={clsx({ 'mobile-results-redesign': mobileResultsRedesign })} gap={16}>
      {flights.map((flight, index) => (
        <VerticalSpacer key={flight.id} gap={8}>
          <FlightHeader
            className={clsx({ 'mobile-results-redesign': mobileResultsRedesign })}
            direction="horizontal"
            gap="0 4"
            horizontalAlign="space-between"
            verticalAlign="center"
          >
            {!mobileResultsRedesign && <Heading variant="heading6">
              {intl.formatMessage(
                {
                  id: 'd6Y5k_umbyz4VTzvzdwR',
                  defaultMessage: 'Flight {number}',
                  description: 'Flight number label in multi-city flight search in the flights section',
                },
                { number: index + 1 },
              )}
            </Heading>}

            {mobileResultsRedesign && <BodyText variant="medium" weight="bold">
              {intl.formatMessage(
                {
                  id: 'd6Y5k_umbyz4VTzvzdwR',
                  defaultMessage: 'Flight {number}',
                  description: 'Flight number label in multi-city flight search in the flights section',
                },
                { number: index + 1 },
              )}
            </BodyText>}

            {(mobileResultsRedesign ? index > 0 && showRemoveButton : showRemoveButton) && (
              <>
                {mobileResultsRedesign && <IconButton
                  kind="tertiary"
                  variant="default"
                  size="small"
                  aria-label={intl.formatMessage({
                    id: 'd-DIEfOEA7qpUl3Wz_o8',
                    defaultMessage: 'Remove flight',
                    description: 'Accessible label for the button that removes a flight leg from a multi-city flight search',
                  })}
                  onClick={() => handleRemoveFlight(flight.id)}
                >
                  <LineTimesIcon size="S" />
                </IconButton>}

                {!mobileResultsRedesign && (
                  <TextButton
                    kind="tertiary"
                    onClick={() => handleRemoveFlight(flight.id)}
                  >
                    {intl.formatMessage({
                      id: 'CSVuWo5_t62exrgMvmay',
                      defaultMessage: 'Remove',
                      description: 'Button text to remove a flight in multi-city flight search',
                    })}
                  </TextButton>
                )}
              </>
            )}
          </FlightHeader>

          <ButtonsContainer
            className={clsx({ 'mobile-results-redesign': mobileResultsRedesign })}
            direction="vertical"
          >
            <AirportsContainer
              className={clsx({ 'mobile-results-redesign': mobileResultsRedesign })}
              direction="vertical"
            >
              <FlightSearchResponsiveAirportField
                inlineSearch={!!mobileResultsRedesign && isTabletViewport}
                flightId={flight.id}
                direction="departure"
                readOnly={readOnlyAirports}
                onNext={onArrivalSelect}
                value={getAirportDisplayText(flight.departureAirport)}
                label={mobileResultsRedesign ? undefined : departureAirportLabel}
                aria-label={departureAirportLabel}
                required
                placeholder={intl.formatMessage({
                  id: 'HRi6-dtcAk0WQRsHbA_y',
                  defaultMessage: 'Search by city or airport',
                  description: 'Placeholder text for airport search field in the search interface',
                })}
                onClick={() => onDepartureSelect(flight.id)}
                requiredErrorMessage={intl.formatMessage({
                  id: 'XT7iAbC3Uf_TMuFN09pG',
                  defaultMessage: 'Please enter a departure',
                  description: 'Error message when departure airport is not selected',
                })}
                hasError={flight.showDepartureError}
                className="airport-select"
                ref={(node) => {
                  departingRefs.current[index] = node
                }}
              />

              <FlightSearchResponsiveAirportField
                inlineSearch={!!mobileResultsRedesign && isTabletViewport}
                flightId={flight.id}
                direction="arrival"
                readOnly={readOnlyAirports}
                onNext={onDateSelect}
                value={getAirportDisplayText(flight.arrivalAirport)}
                label={mobileResultsRedesign ? undefined : arrivalAirportLabel}
                aria-label={arrivalAirportLabel}
                required
                placeholder={intl.formatMessage({
                  id: 'HRi6-dtcAk0WQRsHbA_y',
                  defaultMessage: 'Search by city or airport',
                  description: 'Placeholder text for airport search field in the search interface',
                })}
                onClick={() => onArrivalSelect(flight.id)}
                requiredErrorMessage={intl.formatMessage({
                  id: '0gkTK6pcWoE5eD7RD9w7',
                  defaultMessage: 'Please enter a destination',
                  description: 'Error message when destination airport is not selected',
                })}
                hasError={flight.showDestinationError}
                className="airport-select"
              />

              <ExchangeButtonContainer>
                <IconButton variant="default" kind="tertiary" size="medium" shape="circle" className="switch" onClick={() => onSwapAirports(flight.id)} disabled={readOnlyAirports}>
                  <LineArrowsSwapVerticalIcon size="M" />
                </IconButton>
              </ExchangeButtonContainer>
            </AirportsContainer>

            <div
              ref={(node) => {
                datePickerAnchorRefs.current[flight.id] = node
              }}
            >
              <SearchFormFieldGroup>
                <SearchFormField
                  label={mobileResultsRedesign ? undefined : departingDateLabel}
                  aria-label={departingDateLabel}
                  placeholder={intl.formatMessage({
                    id: 'Xnhjjfb2SQAvUwMEDgTX',
                    defaultMessage: 'Select date',
                    description: 'Placeholder text for date selection field when field is empty',
                  })}
                  required
                  requiredErrorMessage={intl.formatMessage({
                    id: 'QjCDP-dTWZM0XJzm4nhw',
                    defaultMessage: 'Please select travel dates',
                    description: 'Error notification displayed when travel dates are not selected',
                  })}
                  value={flight.checkinDate ? intlDateFormatter(flight.checkinDate, INTL_DMY_CASUAL_FORMAT, intl.locale) : undefined}
                  onClick={() => onDateSelect(flight.id)}
                  hasError={flight.showTravelDatesError}
                  className="date-select"
                />

                {fareType.value === FlightsFareTypes.RETURN && (
                  <SearchFormField
                    label={intl.formatMessage({
                      id: 'wM-9lNMdAZJhxb1b-Ntf',
                      defaultMessage: 'Returning',
                      description: 'Form field label for returning date field in flight search',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'Xnhjjfb2SQAvUwMEDgTX',
                      defaultMessage: 'Select date',
                      description: 'Placeholder text for date selection field when field is empty',
                    })}
                    required
                    requiredErrorMessage={intl.formatMessage({
                      id: 'QjCDP-dTWZM0XJzm4nhw',
                      defaultMessage: 'Please select travel dates',
                      description: 'Error notification displayed when travel dates are not selected',
                    })}
                    value={flight.checkoutDate ? intlDateFormatter(flight.checkoutDate, INTL_DMY_CASUAL_FORMAT, intl.locale) : undefined}
                    onClick={() => onDateSelect(flight.id)}
                    hasError={flight.showTravelDatesError}
                  />
                )}
              </SearchFormFieldGroup>
            </div>
          </ButtonsContainer>
        </VerticalSpacer>
      ))}

      <TextButton
        kind={mobileResultsRedesign ? 'tertiary' : 'secondary'}
        variant="default"
        startIcon={mobileResultsRedesign ? <LinePlusCircleIcon size="S" /> : <LinePlusIcon />}
        size="medium"
        onClick={handleAddFlight}
      >
        {addFlightLabel}
      </TextButton>
    </Form>
    {mobileResultsRedesign && isTabletViewport && datePickerAnchor && <FlightSearchDateSelectSheet
      key={state.selectedFlightId}
      id={state.selectedFlightId || flights[0]?.id || ''}
      anchorRef={datePickerAnchorRef}
      isOpen={state.activeMenu === StandaloneFlightMenu.date && !!state.selectedFlightId}
      onClose={closeTabletDatePicker}
      flightDeals={flightDeal ? [flightDeal] : undefined}
      mobileResultsRedesign
    />}
  </>
}

export default FlightSearchWidgetMobileMultiCityForm
